import { formatRailsTimestamp } from 'lib/utilities'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentStatus } from '../slices/globalSlice'
import { Collapse } from './RequestInfo'
import clsx from 'clsx'

const MAPPING_STATUS = {
  'price set': 'awaiting payment',
  'recorrected price set': 'awaiting payment',
  'email sent': 'sent',
}

const mapStatus = (status, price) => {
  let mappedStatus = MAPPING_STATUS[status]

  if (!mappedStatus) return null
  if (mappedStatus === 'awaiting payment' && price === 0) mappedStatus = status

  return mappedStatus
}

const StatusRow = ({ status }) => {
  const [expanded, setExpanded] = useState(false)

  const formattedDate = formatRailsTimestamp(status?.created_at, {
    year: '2-digit',
    timeZoneName: 'short',
    dateOnly: true,
  })?.toLowerCase()

  const formattedTime = formatRailsTimestamp(status?.created_at, {
    minute: '2-digit',
    timeZoneName: 'short',
    timeOnly: true,
  })?.replace(/AM|PM/, (m) => m.toLowerCase())

  const colorClass = status?.to_status === 'canceled' ? 'dome-color-red-alert' : 'dome-color-dark-grey'

  return (
    <li>
      <button
        type="button"
        className="dome-d-flex dome-align-center dome-justify-between dome-cursor-pointer"
        style={{ background: 'none', border: 'none', padding: 0, width: '100%', textAlign: 'left' }}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span className={clsx('dome-p2 dome-flex-1', colorClass)}>{status?.to_status}</span>
        <span className={clsx('dome-p2', colorClass)}>{formattedDate}</span>
        <i
          className={clsx('fa-regular', colorClass, expanded ? 'fa-chevron-up' : 'fa-chevron-down')}
          style={{ marginLeft: 6 }}
        />
      </button>
      {expanded && (
        <div className="dome-d-flex dome-justify-between dome-align-center dome-gap6">
          <span className="dome-p2 dome-color-med-grey dome-overflow-ellipsis dome-flex-1">
            {status?.created_by || ''}
          </span>
          <span className="dome-p2 dome-color-med-grey">{formattedTime}</span>
        </div>
      )}
    </li>
  )
}

export const AutoQuestionnaireStatuses = () => {
  const statuses = useSelector((state) => state.global.statuses)

  if (statuses.length === 0) {
    return null
  }

  return (
    <Collapse title="status history">
      <div style={{ padding: '6px 12px', background: '#F9F9F9', marginTop: 6 }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="dome-d-flex dome-flex-column dome-gap6">
          {statuses.map((status) => (
            <StatusRow key={status?.created_at} status={status} />
          ))}
        </ul>
      </div>
    </Collapse>
  )
}

export default function AutoQuestionnaireStatus() {
  const [openTooltip, setOpenTooltip] = useState(false)
  const currentStatus = useSelector(selectCurrentStatus)
  const price = useSelector((state) => state.price.total)

  const hasTooltip = (currentStatus === 'price set' || currentStatus === 'recorrected price set') && price > 0

  const statusStyle = useMemo(() => {
    switch (currentStatus) {
      case 'canceled':
        return 'dome-p2 dome-text-w500 dome-color-red-alert'
      case 'email sent':
        return 'dome-p2 dome-text-w500 dome-color-go-green'
      default:
        return 'dome-p2 dome-color-dark-grey'
    }
  }, [currentStatus])

  return (
    <div className="dome-d-flex dome-gap3 dome-align-baseline">
      <span className="dome-p1 dome-color-dark-grey dome-text-w500">status:</span>
      <div className="dome-d-flex dome-align-end dome-gap6 dome-relative">
        <span className={statusStyle}>{mapStatus(currentStatus, price) ?? currentStatus}</span>
        {hasTooltip && (
          <>
            <span
              className="dome-cursor-pointer"
              onMouseMove={() => setOpenTooltip(true)}
              onMouseLeave={() => setOpenTooltip(false)}
            >
              <i
                className={clsx(
                  'fa-light fa-circle-question',
                  openTooltip ? 'dome-color-sage' : 'dome-color-dark-green'
                )}
              ></i>
            </span>
            {openTooltip && (
              <div className="processing-page-status-tooltip">
                <div className="dome-d-flex dome-flex-column">
                  <span className="dome-p2 dome-color-darkest-grey dome-text-w400">No payment yet.</span>
                  <span className="dome-p3 dome-color-darkest-grey dome-text-w300">
                    Questionnaire can be sent after payment has been made.
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
